
import React from 'react';
import { cn } from '../lib/utils';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children, variant = 'primary', size = 'md', isLoading, className, disabled, ...props
}) => {
  const base = "inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-deep-0 disabled:opacity-40 disabled:pointer-events-none active:scale-[0.98]";

  const variants = {
    primary: "bg-accent text-deep-0 hover:bg-accent/90 shadow-[0_0_20px_rgba(245,158,11,0.2)]",
    secondary: "bg-white text-deep-0 hover:bg-white/90 shadow-sm",
    outline: "bg-transparent border border-white/10 hover:border-accent hover:text-accent",
    ghost: "bg-transparent hover:bg-white/5",
    glass: "bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 text-white",
    danger: "bg-red-500 text-white hover:bg-red-600 shadow-[0_0_20px_rgba(239,68,68,0.2)]"
  };

  const sizes = {
    sm: "h-9 px-4 text-xs tracking-wider uppercase",
    md: "h-11 px-6 text-sm tracking-wide",
    lg: "h-12 md:h-14 px-8 md:px-10 text-sm md:text-base font-black uppercase tracking-widest",
    xl: "h-14 md:h-16 px-10 md:px-12 text-sm md:text-base font-black uppercase tracking-widest"
  };

  return (
    <button
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && <Loader2 className="w-5 h-5 mr-3 animate-spin" />}
      {children}
    </button>
  );
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label?: string, error?: string }> = ({
  label, error, className, id, ...props
}) => {
  return (
    <div className="w-full space-y-2">
      {label && <label htmlFor={id} className="text-xs font-bold uppercase tracking-widest text-white/50 pl-1">{label}</label>}
      <input
        id={id}
        className={cn(
          "flex h-12 w-full rounded-xl border border-white/10 bg-deep-surface px-4 py-2 text-sm text-white placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:border-accent disabled:cursor-not-allowed disabled:opacity-50 transition-all",
          error && "border-red-500/50 focus-visible:ring-red-500/20",
          className
        )}
        {...props}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
};

export const Card: React.FC<{ children: React.ReactNode, className?: string, hover?: boolean }> = ({ children, className, hover = true }) => (
  <div className={cn(
    "bg-deep-surface/40 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden transition-all duration-500",
    hover && "hover:border-white/20 hover:bg-deep-surface/60",
    className
  )}>
    {children}
  </div>
);

export const Badge: React.FC<{ children: React.ReactNode, variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent', className?: string }> = ({ children, variant = 'default', className }) => {
  const styles = {
    default: "bg-white/5 text-white/70 border border-white/10",
    success: "bg-green-500/10 text-green-400 border border-green-500/20",
    warning: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
    danger: "bg-red-500/10 text-red-400 border border-red-500/20",
    info: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
    accent: "bg-accent/10 text-accent border border-accent/20"
  };
  return (
    <span className={cn("px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest", styles[variant], className)}>
      {children}
    </span>
  );
};
